import { FC, useState, MouseEvent, useMemo, useEffect } from 'react';
import { GridSortModel } from '@mui/x-data-grid-premium';
import {
  SortAscendingIcon,
  SortCustomIcon,
  SortDescendingIcon,
} from '../../../../../../../svg/custom.svg';
import { CUSTOM_SORT_MODEL, defaultOrderState } from '../../defaultOrder.state';
import { Fade, Menu, MenuItem, Button, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';

export const SortControl: FC<ISortControlProps> = ({ disabled }) => {
  const [sortModel, setSortModel] = useState<GridSortModel | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const subState = defaultOrderState.sub.state().subscribe((state) => {
      setSortModel(state.sortModel);
    });

    return () => {
      subState.unsubscribe();
    };
  }, []);

  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);
  const SortIcon = useMemo(() => {
    const sortDirection = sortModel?.[0]?.sort;
    switch (sortDirection) {
      case 'asc':
        return SortAscendingIcon;
      case 'desc':
        return SortDescendingIcon;
      case null:
        return SortCustomIcon;
      default:
        return SortCustomIcon;
    }
  }, [sortModel]);
  const sortLabel = useMemo(() => {
    if (!sortModel) return '';
    const sortDirection = sortModel?.[0]?.sort;
    const sortField = sortModel?.[0]?.field;
    if (sortField === 'articleNo' && sortDirection === 'asc') {
      return `${t('common.number')} (${t('customer.ascending')})`;
    }
    if (sortField === 'articleNo' && sortDirection === 'desc') {
      return `${t('common.number')} (${t('customer.descending')})`;
    }
    if (sortField === 'description' && sortDirection === 'asc') {
      return `${t('common.description')} (A-Z)`;
    }
    if (sortField === 'description' && sortDirection === 'desc') {
      return `${t('common.description')} (Z-A)`;
    }
    if (sortField === CUSTOM_SORT_MODEL[0].field) {
      return t('common.custom');
    }
    return '';
  }, [sortModel, t]);

  const handleSort = (v: GridSortModel) => {
    defaultOrderState.pub.sortPositions(v);
    setAnchorEl(null);
  };

  const handleSwitchMenu = (event?: MouseEvent<HTMLElement>) => {
    setAnchorEl(event ? event.currentTarget : null);
  };

  return (
    <>
      <SButton
        variant='text'
        color='primary'
        title={t('customer.sort_by')}
        onClick={handleSwitchMenu}
        disabled={disabled}
        startIcon={<SortIcon className='sortIcon' />}
      >
        {t('customer.sort_by')}
        {sortLabel && `: ${sortLabel}`}
      </SButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        keepMounted
        onClose={() => handleSwitchMenu()}
        TransitionComponent={Fade}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <MenuItem
          selected={sortModel?.[0]?.field === 'articleNo' && sortModel?.[0]?.sort === 'asc'}
          onClick={() => handleSort([{ field: 'articleNo', sort: 'asc' }])}
        >
          {t('common.number')} ({t('customer.ascending')})
        </MenuItem>
        <MenuItem
          selected={sortModel?.[0]?.field === 'articleNo' && sortModel?.[0]?.sort === 'desc'}
          onClick={() => handleSort([{ field: 'articleNo', sort: 'desc' }])}
        >
          {t('common.number')} ({t('customer.descending')})
        </MenuItem>
        <MenuItem
          selected={sortModel?.[0]?.field === 'description' && sortModel?.[0]?.sort === 'asc'}
          onClick={() => handleSort([{ field: 'description', sort: 'asc' }])}
        >
          {t('common.description')} (A-Z)
        </MenuItem>
        <MenuItem
          selected={sortModel?.[0]?.field === 'description' && sortModel?.[0]?.sort === 'desc'}
          onClick={() => handleSort([{ field: 'description', sort: 'desc' }])}
        >
          {t('common.description')} (Z-A)
        </MenuItem>
      </Menu>
    </>
  );
};

const SButton = styled(Button)({
  'svg.sortIcon': {
    fontSize: '24px',
    fill: 'currentcolor',
  },
});

interface ISortControlProps {
  disabled: boolean;
}
